import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { toast } from 'react-toastify'
import to from 'await-to-js'
import { Form } from 'react-bootstrap'

import { Modal } from 'components/UI/Modal/Modal'
import { TextInput } from 'components/FormUtils/TextInput'
import { ErrorLabel } from 'components/FormUtils/ErrorLabel'
import { handleFormError } from 'utils/handleFormError'

import {
  type SenderEmail,
  type UpdateSenderEmailPayload,
  useUpdateSenderEmail,
} from 'features/campaigns/sender_emails_api'

interface EditSenderEmailModalProps {
  isOpen: boolean
  onClose: () => void
  senderEmail: SenderEmail | null
}

interface EditSenderEmailForm {
  name: string
  daily_limit: number
  email_signature: string
}

export function EditSenderEmailModal({
  isOpen,
  onClose,
  senderEmail,
}: EditSenderEmailModalProps) {
  const updateMutation = useUpdateSenderEmail()

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty, errors },
    setError,
    reset,
  } = useForm<EditSenderEmailForm>({
    defaultValues: {
      name: '',
      daily_limit: 0,
      email_signature: '',
    },
    mode: 'onChange',
  })

  // Reset form when senderEmail changes
  useEffect(() => {
    if (senderEmail) {
      reset({
        name: senderEmail.name || '',
        daily_limit: senderEmail.daily_limit || 0,
        email_signature: senderEmail.email_signature || '',
      })
    }
  }, [senderEmail, reset])

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset({
        name: '',
        daily_limit: 0,
        email_signature: '',
      })
    }
  }, [isOpen, reset])

  const onSubmit = handleSubmit(async (values) => {
    if (!senderEmail) return

    const payload: UpdateSenderEmailPayload = {}

    // Only include changed fields
    if (values.name !== senderEmail.name) {
      payload.name = values.name
    }
    if (values.daily_limit !== senderEmail.daily_limit) {
      payload.daily_limit = values.daily_limit
    }
    if (values.email_signature !== senderEmail.email_signature) {
      payload.email_signature = values.email_signature
    }

    // If nothing changed, just close
    if (Object.keys(payload).length === 0) {
      onClose()
      return
    }

    const [err] = await to(
      updateMutation.mutateAsync({
        senderEmailId: senderEmail.id,
        payload,
      })
    )

    if (err) {
      handleFormError(err, setError)
      return
    }

    toast.success('Sender email updated successfully')
    onClose()
  })

  return (
    <Modal
      open={isOpen}
      title="Edit Sender Email"
      description={
        senderEmail ? `Editing settings for ${senderEmail.email}` : ''
      }
      onOpenChange={(open) => !open && onClose()}
      loading={isSubmitting}
      blockAccept={!isDirty}
      onAccept={onSubmit}
      acceptButtonText="Save Changes"
      size="md"
    >
      <div className="space-y-4">
        <TextInput
          name="name"
          control={control}
          label="Display Name"
          placeholder="Enter display name"
          rules={{ required: 'Name is required' }}
        />

        <TextInput
          name="daily_limit"
          control={control}
          label="Daily Limit"
          placeholder="Enter daily send limit"
          type="number"
          rules={{
            required: 'Daily limit is required',
            min: { value: 1, message: 'Minimum value is 1' },
            max: { value: 1000, message: 'Maximum value is 1000' },
          }}
        />

        <div className="flex flex-col w-full mt-[10px]">
          <div className="mb-1">
            <label
              htmlFor="email_signature"
              className="text-base font-medium text-gray-800 select-none"
            >
              Email Signature
              <span className="italic text-sm font-normal"> - optional</span>
            </label>
          </div>
          <Controller
            name="email_signature"
            control={control}
            render={({ field }) => (
              <Form.Control
                as="textarea"
                id="email_signature"
                rows={6}
                placeholder="Enter email signature (HTML supported)"
                {...field}
              />
            )}
          />
          {errors.email_signature && (
            <ErrorLabel message={errors.email_signature.message || ''} />
          )}
        </div>
      </div>
    </Modal>
  )
}
